import type { PostgrestResponse } from "@supabase/postgrest-js";
import { PostgrestClient } from "@supabase/postgrest-js";
import Fuse from "fuse.js";
import { isString } from "lodash-es";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import type { DatabaseWithOptions } from "../../../__definitions__/database-definitions";
import { useSupabaseLoader } from "../../../server/supabase/hooks";
import type { ISO8601 } from "../../../utils/iso8601";
import { fromISO8601 } from "../../../utils/iso8601";
import type { UUID } from "../../../utils/uuid";
import type { FilteredMomentInfo, SearchAttributes } from "./utils";
import { useSetQueryParams } from "./utils";

export function useClientFilteredMoments<T extends SearchAttributes>(
  get: (
    client: PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"]>,
    account_id: UUID,
  ) => Promise<PostgrestResponse<T>>,
): FilteredMomentInfo<T> {
  const setQueryParams = useSetQueryParams();
  const filterFn = useMomentQueryFilterFunction<T>();

  const info = useSupabaseLoader(
    async ({ supabase, account_id }) => {
      const { data, error } = await get(supabase, account_id);
      if (error) throw new Error(error.message);
      return filterFn(data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterFn],
  );

  return {
    loading: info.loading,
    moments: info.data ?? undefined,
    error: info.error !== null,
    setQueryParams,
    refresh: info.reload,
  };
}

export function useMomentQueryFilterFunction<T extends SearchAttributes>(): (moments: T[]) => T[] {
  const [searchParams] = useSearchParams();

  const filters = useMemo(
    () => ({
      programs: searchParams.getAll("program"),
      statuses: searchParams.getAll("status"),
      channels: searchParams.getAll("channel"),
      events: searchParams.getAll("event"),
      segments: searchParams.getAll("segment"),
      q: searchParams.get("q"),
      start: searchParams.get("start"),
      end: searchParams.get("end"),
    }),
    [searchParams],
  );

  const fn = useCallback(
    (moments: T[]) => {
      let filteredMoments = moments;
      const { programs, statuses, channels, events, segments, q, start, end } = filters;

      if (programs.length > 0) {
        filteredMoments = filteredMoments.filter((m) => programs.includes(m.program_id || ""));
      }

      if (statuses.length > 0) {
        filteredMoments = filteredMoments.filter((m) => statuses.includes(m.status || ""));
      }

      if (channels.length > 0) {
        filteredMoments = filteredMoments.filter(
          (m) =>
            m.channel &&
            channels.map((x) => (x === "email" ? "work_email" : x)).includes(m.channel),
        );
      }

      if (events.length > 0) {
        filteredMoments = filteredMoments.filter((m) =>
          events.includes(m.schedule?.event_key || ""),
        );
      }

      if (segments.length > 0) {
        filteredMoments = filteredMoments.filter(
          (m) => m.segment === null || segments.includes(m.segment?.id || ""),
        );
      }

      if (isString(q) && q.trim().length > 0) {
        const fuse = new Fuse(filteredMoments, { keys: ["title"], useExtendedSearch: true });
        filteredMoments = fuse.search(q).map((r) => r.item);
      }

      if (isString(start)) {
        filteredMoments = filteredMoments.filter((m) =>
          m.when ? fromISO8601(m.when) >= fromISO8601(start as ISO8601) : true,
        );
      }

      if (isString(end)) {
        filteredMoments = filteredMoments.filter((m) =>
          m.when ? fromISO8601(m.when) <= fromISO8601(end as ISO8601) : true,
        );
      }

      return filteredMoments;
    },
    [filters],
  );

  return fn;
}
